<template>
  <div>
    <el-card>
      <el-form class="form_pad" :inline="true">
        <el-row class="clear_fix">
          <el-form-item :label="$t('subAccount.account.keyword')">
            <el-input
              v-model.trim="filters.keyword"
              :style="{ width: keywordWidth }"
              :placeholder="$t('subAccount.account.keywordPh')"
            />
          </el-form-item>
          <el-form-item :label="$t('subAccount.account.role')">
            <multi-select
              v-model="filters.roleIds"
              :options="roleOptions"
              :placeholder="$t('subAccount.account.rolePh')"
              width="auto"
              :min-width="180"
            />
          </el-form-item>
          <el-form-item :label="$t('table.status')">
            <el-select
              v-model="filters.status"
              clearable
              :style="{ width: statusWidth }"
              :placeholder="$t('subAccount.account.statusPh')"
            >
              <el-option :label="$t('subAccount.account.enabled')" :value="STATUS_ON" />
              <el-option :label="$t('subAccount.account.disabled')" :value="STATUS_OFF" />
            </el-select>
          </el-form-item>
        </el-row>
        <el-row class="search-btns">
          <el-button type="primary" icon="el-icon-search" @click="search">{{ $t('common.search') }}</el-button>
        </el-row>
      </el-form>
    </el-card>

    <div class="mb10">
      <el-button type="primary" class="handle-del mr10" @click="handleCreate">{{ $t('subAccount.account.add') }}</el-button>
    </div>

    <el-card>
      <div class="table-container">
        <el-table :data="list" class="table" v-loading="loading">
          <el-table-column :label="$t('subAccount.account.email')" min-width="200" align="left">
            <template slot-scope="scope">
              <span :class="{ mr10: isMain(scope.row) }">{{ scope.row.email }}</span>
              <!-- 主账号固定在第一行，不可编辑/停用/删除 -->
              <el-tag v-if="isMain(scope.row)" size="mini">{{ $t('subAccount.account.mainTag') }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="account_name" :label="$t('subAccount.account.name')" min-width="120" align="left" />

          <el-table-column :label="$t('subAccount.account.role')" min-width="140" align="left">
            <template slot-scope="scope">
              <el-tag v-if="isMain(scope.row)" type="info" size="small">{{ $t('subAccount.account.allPerm') }}</el-tag>
              <span v-else>{{ scope.row.role_name }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('table.status')" width="110" align="left">
            <template slot-scope="scope">
              <el-tag v-if="isMain(scope.row)" type="success" size="mini">{{ $t('subAccount.account.enabled') }}</el-tag>
              <el-switch
                v-else
                :value="scope.row.status === STATUS_ON"
                :disabled="statusPending === scope.row.id"
                @change="toggleStatus(scope.row, $event)"
              />
            </template>
          </el-table-column>

          <el-table-column :label="$t('subAccount.role.createdAt')" width="150" align="left">
            <template slot-scope="scope">{{ scope.row.create_time | datetime }}</template>
          </el-table-column>
          <el-table-column :label="$t('subAccount.role.updatedAt')" width="150" align="left">
            <template slot-scope="scope">{{ scope.row.update_time | datetime }}</template>
          </el-table-column>

          <el-table-column :label="$t('table.operation')" width="130" align="left">
            <template slot-scope="scope">
              <span v-if="isMain(scope.row)">—</span>
              <template v-else>
                <el-button type="text" size="large" @click="handleEdit(scope.row)">{{ $t('operation.edit') }}</el-button>
                <el-button type="text" size="large" @click="handleRemove(scope.row)">{{ $t('operation.delete') }}</el-button>
              </template>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination">
          <el-pagination
            background
            :current-page="query.page"
            :page-size="query.page_size"
            layout="total, sizes, prev, pager, next"
            :total="total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <account-dialog
      v-if="dialogVisible"
      :visible.sync="dialogVisible"
      :account="row"
      :roles="roles"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import { datetime } from '@/utils/subAccount/format'
import { fitWidth } from '@/utils/subAccount/fitWidth'
import MultiSelect from '@/views/abroad/components/MultiSelect'
import AccountDialog from './AccountDialog'

// 0 = 启用，1 = 停用
const STATUS_ON = 0
const STATUS_OFF = 1

export default {
  name: 'SubAccountAccountList',
  components: { MultiSelect, AccountDialog },
  props: {
    // 从角色页「账号数」跳过来时带的角色，进页面就按它筛一次
    roleId: {
      type: [Number, String],
      default: null
    },
    roles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      STATUS_ON: STATUS_ON,
      STATUS_OFF: STATUS_OFF,
      loading: false,
      list: [],
      total: 0,
      statusPending: null,
      filters: { keyword: '', roleIds: [], status: '' },
      query: { page: 1, page_size: 20 },
      dialogVisible: false,
      row: null
    }
  },
  filters: {
    datetime
  },
  computed: {
    // 按提示文字撑宽，换语言时跟着重算
    keywordWidth() {
      return fitWidth(this.$t('subAccount.account.keywordPh'), { min: 180 })
    },
    // 选中后显示的是选项文字，所以选项也一起量，取最宽的
    statusWidth() {
      return fitWidth([
        this.$t('subAccount.account.statusPh'),
        this.$t('subAccount.account.enabled'),
        this.$t('subAccount.account.disabled')
      ], { type: 'select', min: 140 })
    },
    roleOptions() {
      return this.roles.map(r => ({ value: r.id, label: r.name }))
    }
  },
  watch: {
    roles() {
      // 角色被删除后重新拉了一次
      // 否则触发器上已经显示不出它了，请求里却还带着它的 id，列表被筛空也看不出原因
      const ids = this.roles.map(r => r.id)
      const next = this.filters.roleIds.filter(id => ids.indexOf(id) !== -1)
      if (next.length !== this.filters.roleIds.length) this.filters.roleIds = next
    }
  },
  // 列表数据由父页面在切到本 tab 时拉，这里只放好筛选条件
  created() {
    this.applyRoleId(this.roleId)
  },
  methods: {
    isMain(row) {
      return !!row.is_main
    },
    applyRoleId(val) {
      const id = val === null || val === undefined || val === '' ? null : Number(val)
      this.filters.roleIds = id ? [id] : []
      this.query.page = 1
    },
    buildParams() {
      const f = this.filters
      const params = { page: this.query.page, page_size: this.query.page_size }
      if (f.keyword) params.keyword = f.keyword
      if (f.roleIds.length) params.role_ids = f.roleIds
      if (f.status === STATUS_ON || f.status === STATUS_OFF) params.status = f.status
      return params
    },
    getList() {
      this.loading = true
      this.__postData('subAccountList', this.buildParams(), res => {
        this.loading = false
        if (res && res.code == 1) {
          this.$message.error(res.msg)
          return
        }
        this.list = (res && res.list) || []
        this.total = (res && res.total) || 0
      }, errmsg => {
        this.loading = false
        this.$message.error(errmsg)
      }).catch(() => {
        this.loading = false
      })
    },
    search() {
      this.query.page = 1
      this.getList()
    },
    handleCurrentChange(p) {
      this.query.page = p
      this.getList()
    },
    handleSizeChange(size) {
      this.query.page = 1
      this.query.page_size = size
      this.getList()
    },
    handleCreate() {
      this.row = null
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.row = row
      this.dialogVisible = true
    },
    onSaved() {
      this.getList()
    },
    toggleStatus(row, on) {
      const status = on ? STATUS_ON : STATUS_OFF
      this.statusPending = row.id
      this.__postData('subAccountStatus', { id: row.id, status: status }, res => {
        this.statusPending = null
        if (res && res.code == 1) {
          this.$message.error(res.msg)
          return
        }
        row.status = status
        this.$message.success(
          this.$t(status === STATUS_ON ? 'subAccount.account.statusOn' : 'subAccount.account.statusOff', { email: row.email })
        )
        this.getList()
      }, errmsg => {
        this.statusPending = null
        this.$message.error(errmsg)
      }).catch(() => {
        this.statusPending = null
      })
    },
    handleRemove(row) {
      const h = this.$createElement
      this.$confirm(
        h('i18n', { props: { path: 'subAccount.account.deleteBody', tag: 'span' } }, [
          h('strong', { slot: 'name' }, row.account_name),
          h('span', { slot: 'email' }, row.email)
        ]),
        this.$t('subAccount.account.deleteTitle'),
        {
          confirmButtonText: this.$t('operation.delete'),
          cancelButtonText: this.$t('cancel'),
          confirmButtonClass: 'el-button--danger',
          type: 'warning'
        }
      )
        .then(() => {
          this.__postData('subAccountDelete', { id: row.id }, res => {
            if (res && res.code == 1) {
              this.$message.error(res.msg)
              return
            }
            this.$message.success(this.$t('subAccount.account.deleted'))
            // 删的是这一页最后一条：退回上一页，不然停在一个空页上
            if (this.list.length === 1 && this.query.page > 1) this.query.page--
            this.getList()
          }, errmsg => {
            this.$message.error(errmsg)
          })
        })
        .catch(() => {})
    }
  }
}
</script>

